/**
 * Agents Tab - Read-only view of AI agents
 *
 * Lists all agents with:
 * - Status (active/paused)
 * - Assigned user count
 * - Performance stats (tasks completed, failed, cost)
 * - Detail modal with system prompt and assigned users
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { listAgents, getAgent, type Agent, type AgentDetail } from '@/services/adminApi';

export default function AgentsTab() {
  const { t } = useTranslation('admin');
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedAgent, setSelectedAgent] = useState<AgentDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingDetail, setIsLoadingDetail] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    loadAgents();
  }, []);

  const loadAgents = async () => {
    setIsLoading(true);
    setError('');
    try {
      const data = await listAgents();
      setAgents(data.agents);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('agents.failedToLoad');
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = async (agentId: number) => {
    setIsLoadingDetail(true);
    try {
      const detail = await getAgent(agentId);
      setSelectedAgent(detail);
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : t('agents.failedToLoadDetails');
      alert(errorMessage);
    } finally {
      setIsLoadingDetail(false);
    }
  };

  const formatCost = (cost: number) => {
    return `$${cost.toFixed(2)}`;
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 text-red-600 p-4 rounded-lg">
        {error}
        <button onClick={loadAgents} className="ml-4 underline">{t('agents.retry')}</button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">{t('agents.title')}</h2>
          <p className="text-sm text-gray-500">{t('agents.description')}</p>
        </div>
        {/* Future: Add Create Agent button here */}
      </div>

      {/* Agents List */}
      {agents.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <p className="text-gray-500">{t('agents.noAgentsConfigured')}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {agents.map((agent) => (
            <div
              key={agent.id}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤖</span>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">{agent.name}</h3>
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded-full ${
                      agent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {agent.status}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => handleViewDetails(agent.id)}
                  disabled={isLoadingDetail}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {isLoadingDetail ? '...' : `${t('agents.viewDetails')} →`}
                </button>
              </div>

              {agent.description && (
                <p className="mt-3 text-gray-600">{agent.description}</p>
              )}

              {/* Stats */}
              <div className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">👥 {t('agents.users')}</div>
                  <div className="text-xl font-semibold text-gray-900">{agent.assigned_user_count}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">✅ {t('agents.completed')}</div>
                  <div className="text-xl font-semibold text-green-600">{agent.stats?.tasks_completed || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">❌ {t('agents.failed')}</div>
                  <div className="text-xl font-semibold text-red-600">{agent.stats?.tasks_failed || 0}</div>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <div className="text-xs text-gray-500 mb-1">💰 {t('agents.cost')}</div>
                  <div className="text-xl font-semibold text-gray-900">{formatCost(agent.stats?.total_cost_usd || 0)}</div>
                </div>
              </div>

              <div className="mt-4 text-sm text-gray-400">
                {t('agents.createdLabel')}: {formatDate(agent.created_at)}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Agent Detail Modal */}
      {selectedAgent && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b sticky top-0 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🤖</span>
                  <div>
                    <h2 className="text-xl font-semibold text-gray-900">{selectedAgent.name}</h2>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      selectedAgent.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {selectedAgent.status}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedAgent(null)}
                  className="text-gray-400 hover:text-gray-600 text-2xl"
                >
                  &times;
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Description */}
              {selectedAgent.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agents.descriptionLabel')}</h3>
                  <p className="text-gray-900">{selectedAgent.description}</p>
                </div>
              )}

              {/* System Prompt */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agents.systemPrompt')}</h3>
                <div className="bg-gray-50 rounded-lg p-4 font-mono text-sm text-gray-700 whitespace-pre-wrap">
                  {selectedAgent.system_prompt}
                </div>
              </div>

              {/* Stats */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">{t('agents.performanceStats')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('agents.tasksCompleted')}</div>
                    <div className="text-xl font-semibold text-green-600">{selectedAgent.stats?.tasks_completed || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('agents.tasksFailed')}</div>
                    <div className="text-xl font-semibold text-red-600">{selectedAgent.stats?.tasks_failed || 0}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('agents.totalCost')}</div>
                    <div className="text-xl font-semibold text-gray-900">{formatCost(selectedAgent.stats?.total_cost_usd || 0)}</div>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-3">
                    <div className="text-xs text-gray-500 mb-1">{t('agents.avgCompletion')}</div>
                    <div className="text-xl font-semibold text-gray-900">
                      {selectedAgent.stats?.avg_completion_hours ? `${selectedAgent.stats.avg_completion_hours.toFixed(1)}h` : t('common.notAvailable')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Assigned Users */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">
                  {t('agents.assignedUsers')} ({selectedAgent.assigned_user_count})
                </h3>
                {selectedAgent.assigned_users && selectedAgent.assigned_users.length > 0 ? (
                  <div className="space-y-2">
                    {selectedAgent.assigned_users.map((user) => (
                      <div
                        key={user.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div>
                          <span className="font-medium text-gray-900">{user.full_name || user.username}</span>
                          <span className="text-gray-500 ml-2">@{user.username}</span>
                        </div>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          user.enabled ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {user.enabled ? t('agents.enabled') : t('agents.disabled')}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500">{t('agents.noUsersAssigned')}</p>
                )}
              </div>

              {/* Dates */}
              <div className="text-sm text-gray-400 pt-4 border-t">
                <div>{t('agents.createdLabel')}: {formatDate(selectedAgent.created_at)}</div>
                <div>{t('agents.updated')}: {formatDate(selectedAgent.updated_at)}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
